import { MapPin, Phone, Mail, Compass } from "lucide-react";

export default function Location({ content }) {
  const c = content || {};
  const nearby = c.nearby || [];
  const mapQuery = encodeURIComponent(c.map_query || "12 Collingwood Street East, Meaford, ON");
  const mapSrc = `https://www.google.com/maps?q=${mapQuery}&output=embed`;

  return (
    <section
      id="location"
      data-testid="location-section"
      className="relative py-24 md:py-36 lg:py-44 bg-[var(--bone)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-16">
          <div className="lg:col-span-5">
            <p className="overline mb-6">{c.overline}</p>
            <h2
              data-testid="location-title"
              className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight font-medium"
            >
              {c.title}
            </h2>
            <p className="mt-6 text-[var(--ink-soft)] text-base md:text-lg leading-relaxed max-w-md">
              {c.body}
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-[var(--terracotta)] mt-1 shrink-0" />
                <div>
                  <p className="overline">Address</p>
                  <p className="font-serif-display text-lg mt-1">
                    {c.address_line_1}
                    <br />
                    {c.address_line_2}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={18} className="text-[var(--terracotta)] mt-1 shrink-0" />
                <div>
                  <p className="overline">Leasing</p>
                  <a
                    data-testid="location-phone"
                    href={`tel:${(c.phone || "").replace(/[^0-9+]/g, "")}`}
                    className="font-serif-display text-lg mt-1 block hover:text-[var(--terracotta)] transition-colors"
                  >
                    {c.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={18} className="text-[var(--terracotta)] mt-1 shrink-0" />
                <div>
                  <p className="overline">Email</p>
                  <a
                    data-testid="location-email"
                    href={`mailto:${c.email}`}
                    className="font-serif-display text-lg mt-1 block hover:text-[var(--terracotta)] transition-colors"
                  >
                    {c.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <div className="relative w-full h-[420px] md:h-[560px] overflow-hidden border border-[var(--line)]">
              <iframe
                data-testid="location-map"
                title="Map"
                src={mapSrc}
                className="w-full h-full"
                style={{ border: 0, filter: "grayscale(0.4) contrast(1.05) sepia(0.08)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        {nearby.length > 0 && (
          <div className="border-t border-[var(--line)] pt-12">
            <div className="flex items-center gap-3 mb-8">
              <Compass size={16} className="text-[var(--terracotta)]" />
              <p className="overline">In the neighbourhood</p>
            </div>
            <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8">
              {nearby.map((p, i) => (
                <li key={i} className="reveal">
                  <p className="font-serif-display text-lg leading-tight">{p.name}</p>
                  <p className="overline mt-2 text-[var(--ink-soft)]">{p.distance}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
